import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../utils/hooks";
import {AxiosError} from "axios";
import {message} from "antd";
import {IS_INTEGRATIONS_FEATURE_ENABLED} from "../../services/config";

export function normalizeCalendarStatusPayload(res: {data: unknown}) {
  const d = res.data as
    | {calendarConnected?: boolean; zoomConnected?: boolean}
    | {hasAccessToken?: boolean; needsLogin?: boolean}
    | {has_access_token?: boolean; needs_login?: boolean};

  if (d && "calendarConnected" in d) {
    return {
      has_access_token: Boolean(d.calendarConnected),
      needs_login: !d.calendarConnected,
    };
  }

  if (d && "needsLogin" in d) {
    return {
      has_access_token: Boolean(d.hasAccessToken),
      needs_login: Boolean(d.needsLogin),
    };
  }

  return {
    has_access_token: Boolean((d as {has_access_token?: boolean}).has_access_token),
    needs_login: Boolean((d as {needs_login?: boolean}).needs_login),
  };
}

export function normalizeIntegrationsStatusPayload(res: {data: unknown}) {
  const d = res.data as {
    calendarConnected?: boolean;
    zoomConnected?: boolean;
    metaPixelConnected?: boolean;
  };

  return {
    calendarConnected: Boolean(d?.calendarConnected),
    zoomConnected: Boolean(d?.zoomConnected),
    metaPixelConnected: Boolean(d?.metaPixelConnected),
  };
}

export const useCoachConnectCalendarGoogleSignIn = () => {
  const clientV2 = useClientV2();

  return useMutation<
    {data: {redirectUrl?: string; url?: string}},
    AxiosError,
    string,
    () => void
  >({
    mutationFn: () =>
      clientV2("integrations/google/calendar/login", {
        method: "GET",
      }),

    onError: (error) => {
      message.error(error.message);
    },
  });
};

export const useGetCoachConnectCalendarStatus = (email: string) => {
  const clientV2 = useClientV2();

  return useQuery<
    {
      has_access_token: boolean;
      needs_login: boolean;
    },
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["connect_calendar_status", email],
    queryFn: () =>
      clientV2("integrations/google/calendar/status").then(
        normalizeCalendarStatusPayload
      ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useCompleteCoachConnectCalendar = (
  email: string,
  userId: number,
  code?: string | null,
  scope?: string | null
) => {
  const clientV2 = useClientV2();
  const queryClient = useQueryClient();

  return useQuery<
    boolean | {tokens_generated?: boolean},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["complete_connect_calendar_status", email, userId, code],
    queryFn: () =>
      clientV2("integrations/google/calendar/callback", {
        method: "GET",
        params: {
          code: code!,
          userId,
          email,
          ...(scope ? {scope} : {}),
        },
      }).then((data) => {
        if ((data as {status?: boolean})?.status === false) {
          return Promise.reject(data);
        }
        return data.data;
      }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!code && !!email && userId > 0,
    onSuccess: () => message.success("Calendar connected successfully"),
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ["connect_calendar_status", email]});
      queryClient.invalidateQueries({queryKey: ["integrations_status"]});
    },
  });
};

export const useCoachDisconnectCalendar = () => {
  const clientV2 = useClientV2();
  const queryClient = useQueryClient();

  return useMutation<{data: unknown}, AxiosError, string, () => void>({
    mutationFn: () =>
      clientV2("integrations/google/calendar/disconnect", {
        method: "DELETE",
      }),

    onError: (error) => {
      message.error(error.message);
    },
    onSettled: (d, e, v) => {
      queryClient.invalidateQueries({queryKey: ["connect_calendar_status", v]});
      queryClient.invalidateQueries({queryKey: ["integrations_status"]});
    },
  });
};

export const useGetIntegrationsStatus = () => {
  const clientV2 = useClientV2();

  return useQuery<
    {
      calendarConnected: boolean;
      zoomConnected: boolean;
      metaPixelConnected: boolean;
    },
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["integrations_status"],
    queryFn: () =>
      clientV2("integrations/status").then(normalizeIntegrationsStatusPayload),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: IS_INTEGRATIONS_FEATURE_ENABLED,
  });
};
